import * as React from 'react';
import { withRouter, Link, Redirect } from 'react-router-dom';
import bindAllOfThis from "src/utils/BindThisHelper"; // 'this' binder
import Routes from 'src/core/Routes';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { PageProps } from 'src/models/common/ComponentProps';

import DataTable, {
    DataTableColumnProps,
    Renderer,
    Filterer,
    DataTableButton,
    DataTableButtonReset
} from 'src/components/common/DataTable';

import TableRequestDTO from 'src/models/datatable/TableRequestDTO';
import TableResponseDTO from 'src/models/datatable/TableResponseDTO';
import AcademicStandardDTO from 'src/models/academic/AcademicStandardDTO';
import AcademicStandardFrameworkDTO from 'src/models/academic/AcademicStandardFrameworkDTO';
import AcademicApiController from 'src/api/AcademicApiController';
import { message, Button, Modal, Divider, Tooltip } from 'antd';


export interface AcademicStandardsProps extends PageProps<{}>, LocalizeContextProps { }

interface AcademicStandardsState {
    loading: boolean;
    standardRedirect: boolean;
    frameworkRedirect: boolean;
    standards: AcademicStandardDTO[];
    frameworks: AcademicStandardFrameworkDTO[];
}

class AcademicStandards extends React.Component<AcademicStandardsProps, AcademicStandardsState> {
    private standardDataTableRef: DataTable<AcademicStandardDTO>;
    private frameworkDataTableRef: DataTable<AcademicStandardFrameworkDTO>;

    constructor(props: AcademicStandardsProps) {
        super(props);
        bindAllOfThis(this, AcademicStandards.prototype);

        this.state = {
            loading: true,
            standardRedirect: false,
            frameworkRedirect: false,
            standards: [],
            frameworks: []
        };
    }

    private deleteAcademicStandard(academicStandardId: number): void {
        let ref = this.standardDataTableRef;
        let translate: (id: any) => any = this.props.translate;

        Modal.confirm({
            title: translate('admin.academics.confirmDelete1'),
            okText: translate('admin.common.confirmYes'),
            okType: 'danger',
            cancelText: translate('admin.common.confirmNo'),
            onOk() {
                AcademicApiController.deleteAcademicStandard(academicStandardId).then(result => {
                    message.success(translate('admin.academics.messageDelete1'));
                    ref.refresh();
                }).catch(e => {
                    message.error(this.props.translate('admin.sideNav.messageDeleteError1'));
                });
            },
        });
    }

    private deleteAcademicStandardFramework(academicStandardFrameworkId: number): void {
        let frameworkRef = this.frameworkDataTableRef;
        let standardRef = this.standardDataTableRef;
        let translate: (id: any) => any = this.props.translate;

        Modal.confirm({
            title: translate('admin.academics.confirmDelete2'),
            okText: translate('admin.common.confirmYes'),
            okType: 'danger',
            cancelText: translate('admin.common.confirmNo'),
            content: translate('admin.academics.confirmContent'),
            onOk() {
                AcademicApiController.deleteAcademicStandardFramework(academicStandardFrameworkId).then(result => {
                    message.success(translate('admin.academics.messageDelete2'));
                    frameworkRef.refresh();
                    standardRef.refresh();
                }).catch(e => {
                    message.error(this.props.translate('admin.sideNav.messageDeleteError2'));
                });
            },
        });
    }

    private fetchStandards(requestState: TableRequestDTO, checkEcho: () => boolean, callback: (response: TableResponseDTO<AcademicStandardDTO>) => void): void {
        this.setState({ loading: true });

        AcademicApiController.getAcademicStandardsForDatatable(requestState)
            .then(results => {
                if (!checkEcho()) {
                    return;
                }

                let tableRecords = results.data.results ? results.data.results : [];

                this.setState({
                    standards: tableRecords,
                    loading: false
                });

                callback(results.data);
            });
    }

    private fetchFrameworks(requestState: TableRequestDTO, checkEcho: () => boolean, callback: (response: TableResponseDTO<AcademicStandardFrameworkDTO>) => void): void {
        this.setState({ loading: true });

        AcademicApiController.getAcademicStandardFrameworksForDatatable(requestState)
            .then(results => {
                if (!checkEcho()) {
                    return;
                }

                let tableRecords = results.data.results ? results.data.results : [];

                this.setState({
                    frameworks: tableRecords,
                    loading: false
                });

                callback(results.data);
            });
    }

    private standardTableColumns(): DataTableColumnProps<AcademicStandardDTO>[] {
        let editLink = (text, record: AcademicStandardDTO) => Routes.getRoute(Routes.ADMIN_ADD_EDIT_ACADEMIC_STANDARD, record.academicStandardId);

        return [
            DataTable.StandardColumns.Text('admin.academics.columnHeader1', 'standard'),
            DataTable.StandardColumns.Text('admin.academics.columnHeader2', 'framework'),
            DataTable.StandardColumns.Link('admin.common.editColumnHeader', 'edit', editLink, 'edit'),
            {
                columnProps: {
                    key: 'delete',
                    align: 'center',
                    width: 35,
                    render: (text, record, index) => {
                        return (
                            <Tooltip placement="bottom" title={this.props.translate('admin.common.deleteColumnHeader')} arrowPointAtCenter>
                                <Button shape="circle" icon="delete" onClick={() => this.deleteAcademicStandard(record.academicStandardId)} />
                            </Tooltip>
                        );
                    }
                }
            }
        ];
    }

    private frameworkTableColumns(): DataTableColumnProps<AcademicStandardFrameworkDTO>[] {
        let editLink = (text, record: AcademicStandardFrameworkDTO) => Routes.getRoute(Routes.ADMIN_ADD_EDIT_ACADEMIC_STANDARD_FRAMEWORK, record.academicStandardFrameworkId);

        return [
            DataTable.StandardColumns.Text('admin.academics.columnHeader2', 'framework'),
            DataTable.StandardColumns.Link('admin.common.editColumnHeader', 'edit', editLink, 'edit'),
            {
                columnProps: {
                    key: 'delete',
                    align: 'center',
                    width: 35,
                    render: (text, record, index) => {
                        return (
                            <Tooltip placement="bottom" title={this.props.translate('admin.common.deleteColumnHeader')} arrowPointAtCenter>
                                <Button shape="circle" icon="delete" onClick={() => this.deleteAcademicStandardFramework(record.academicStandardFrameworkId)} />
                            </Tooltip>
                        );
                    }
                }
            }
        ];
    }

    private renderAcademicStandardsDataTable(): JSX.Element {
        if (this.state.frameworks.length > 0) {
            return (
                <div>
                    <h2>{this.props.translate('admin.academics.tableHeader1')}</h2>

                    <DataTable
                        ref={ref => this.standardDataTableRef = ref}
                        serverSide={true}
                        tableProps={{ rowKey: 'academicStandardId', loading: this.state.loading }}
                        columns={this.standardTableColumns()}
                        data={this.state.standards}
                        fetchData={this.fetchStandards}
                        buttonBar={[
                            new DataTableButton('admin.academics.addButton1', () => { this.setState({ standardRedirect: true }); }),
                            new DataTableButtonReset()
                        ]} />

                    <br /><br />
                    <Divider type="horizontal" />
                    <br /><br />
                </div>
            );
        }
    }

    render() {
        if (this.state.standardRedirect) {
            return <Redirect to={Routes.ADMIN_ADD_EDIT_ACADEMIC_STANDARD} push />;
        } else if (this.state.frameworkRedirect) {
            return <Redirect to={Routes.ADMIN_ADD_EDIT_ACADEMIC_STANDARD_FRAMEWORK} push />;
        }

        return (
            <div>
                {this.renderAcademicStandardsDataTable()}

                <h2>{this.props.translate('admin.academics.tableHeader2')}</h2>

                <DataTable
                    ref={ref => this.frameworkDataTableRef = ref}
                    serverSide={true}
                    tableProps={{ rowKey: 'academicStandardFrameworkId', loading: this.state.loading }}
                    columns={this.frameworkTableColumns()}
                    data={this.state.frameworks}
                    fetchData={this.fetchFrameworks}
                    buttonBar={[
                        new DataTableButton('admin.academics.addButton2', () => { this.setState({ frameworkRedirect: true }); }),
                        new DataTableButtonReset()
                    ]} />
            </div>
        );
    }
}

export default withLocalize(AcademicStandards as any) as any;



// WEBPACK FOOTER //
// ./src/pages/admin/Academics.tsx